/* eslint-disable @cspell/spellchecker */
import { getDocsUrl } from "utils/getDocsUrl";

export const INITIAL_TEMPLATE = `# Check the documentation for more information: ${getDocsUrl("/concepts/template")}
#
# Tip: This code-editor has the same engine as VS Code (Monaco).
# Right-click, then click 'Command Palette' to see a list of available commands.
#
# Minimal example: Single stack with an environment variable from input
inputs:
  - id: environment
    name: Target environment
stacks:
  - key: main
    name: \${{ context.deployment.name }}-stack
    vcs:
      reference:
        type: branch
        value: main
      repository: my-terraform-repo
      provider: GITHUB
    vendor:
      terraform:
        manage_state: true
    environment:
      variables:
        - name: TF_VAR_environment
          value: "\${{ inputs.environment }}"
# ---
# Additional examples (uncomment to use):
#
# 1. Select input with options, used to attach a context to the stack
# inputs:
#   - id: target_env
#     name: Deployment target
#     type: select
#     default: dev
#     options:
#       - dev
#       - staging
#       - prod
# stacks:
#   - key: main
#     name: \${{ context.deployment.name }}-stack
#     vcs:
#       reference:
#         type: branch
#         value: main
#       repository: my-terraform-repo
#       provider: GITHUB
#     vendor:
#       terraform:
#         manage_state: true
#     environment:
#       variables:
#         - name: TF_VAR_environment
#           value: \${{ inputs.target_env }}
#     attachments:
#       contexts:
#         - id: \${{ inputs.target_env }}-context
#           priority: 1
#
# 2. Multiple stacks with dependencies
# inputs:
#   - id: environment
#     name: Target environment
# stacks:
#   - key: network
#     name: \${{ context.deployment.name }}-network
#     vcs:
#       reference:
#         type: branch
#         value: main
#       repository: my-terraform-repo
#       project_root: network
#       provider: GITHUB
#     vendor:
#       terraform:
#         manage_state: true
#     environment:
#       variables:
#         - name: TF_VAR_environment
#           value: \${{ inputs.environment }}
#   - key: app
#     name: \${{ context.deployment.name }}-app
#     depends_on:
#       - network
#     vcs:
#       reference:
#         type: branch
#         value: main
#       repository: my-terraform-repo
#       project_root: app
#       provider: GITHUB
#     vendor:
#       terraform:
#         manage_state: true
#     environment:
#       variables:
#         - name: TF_VAR_environment
#           value: \${{ inputs.environment }}
#       stack_dependency_references:
#         - name: TF_VAR_vpc_id
#           from_stack: network
#           output: vpc_id
#
# 3. Comprehensive single stack with all available options
# inputs:
#   - id: environment
#     name: Target environment
#     type: select
#     default: dev
#     options:
#       - dev
#       - staging
#       - prod
#   - id: project_name
#     name: Project name
#     validations:
#       required: true
#       min_length: 3
#       max_length: 50
#   - id: description
#     name: Stack description
#     type: long_text
#   - id: replica_count
#     name: Number of replicas
#     type: number
#     default: 2
#     validations:
#       greater_than_or_equal: 1
#       less_than_or_equal: 10
#   - id: enable_monitoring
#     name: Enable monitoring
#     type: boolean
#     default: true
#   - id: api_key
#     name: API key
#     type: secret
# maps:
#   region_map:
#     dev: us-east-1
#     staging: us-west-2
#     prod: eu-west-1
# stacks:
#   - key: main
#     name: \${{ context.deployment.name }}-\${{ inputs.project_name }}
#     description: \${{ inputs.description }}
#     labels:
#       - "templates/\${{ context.template.name }}"
#       - "env/\${{ inputs.environment }}"
#       - "project/\${{ inputs.project_name }}"
#     administrative: false
#     autodeploy: true
#     autoretry: true
#     secret_masking_enabled: true
#     runner_image: public.ecr.aws/spacelift/runner-terraform:latest
#     worker_pool: 01GQ29K8SYXKZVHPZ4HG00BK2E
#     vcs:
#       reference:
#         type: branch
#         value: "\${{ inputs.environment == 'prod' ? 'main' : 'develop' }}"
#       repository: my-terraform-repo
#       namespace: my-org
#       project_root: infrastructure
#       project_globs:
#         - "modules/**/*"
#         - "shared/**/*"
#       provider: GITLAB
#       id: my-gitlab-integration-id
#     vendor:
#       terraform:
#         manage_state: true
#         version: "1.5.0"
#         workspace: \${{ inputs.environment }}
#         use_smart_sanitization: true
#         workflow_tool: OPEN_TOFU
#     environment:
#       variables:
#         - name: TF_VAR_environment
#           value: \${{ inputs.environment }}
#         - name: TF_VAR_project_name
#           value: \${{ inputs.project_name }}
#         - name: TF_VAR_replica_count
#           value: "\${{ inputs.replica_count }}"
#         - name: TF_VAR_region
#           value: \${{ maps.region_map[inputs.environment] }}
#         - name: TF_VAR_monitoring_enabled
#           value: "\${{ inputs.enable_monitoring }}"
#         - name: API_KEY
#           value: \${{ inputs.api_key }}
#           description: External API key for integrations
#           secret: true
#       mounted_files:
#         - path: config.json
#           content: |
#             {
#               "environment": "\${{ inputs.environment }}",
#               "project": "\${{ inputs.project_name }}",
#               "region": "\${{ maps.region_map[inputs.environment] }}"
#             }
#           description: Application configuration file
#         - path: credentials.json
#           content: |
#             {
#               "api_key": "\${{ inputs.api_key }}"
#             }
#           secret: true
#     hooks:
#       init:
#         before: ["echo 'Initializing Terraform...'"]
#         after: ["echo 'Terraform initialized'"]
#       plan:
#         before: ["echo 'Running plan for \${{ inputs.environment }}...'"]
#         after: ["echo 'Plan complete'"]
#       apply:
#         before: ["echo 'Applying changes to \${{ inputs.environment }}...'"]
#         after: ["./scripts/notify-slack.sh 'Deployment complete'"]
#       destroy:
#         before: ["echo 'WARNING: Destroying resources...'"]
#         after: ["echo 'Resources destroyed'"]
#       perform:
#         before: ["echo 'Performing task...'"]
#         after: ["echo 'Task complete'"]
#       run:
#         after: ["echo 'Run finished'"]
#     schedules:
#       drift:
#         cron:
#           - "0 6 * * *"
#         reconcile: false
#         ignore_state: false
#         timezone: UTC
#       tasks:
#         - command: "terraform plan -refresh-only"
#           cron:
#             - "0 12 * * 1"
#           timezone: America/New_York
#     attachments:
#       contexts:
#         - id: shared-context
#           priority: 0
#         - id: \${{ inputs.environment }}-context
#           priority: 1
#       clouds:
#         aws:
#           id: aws-integration-id
#           read: true
#           write: true
#         azure:
#           id: azure-integration-id
#           read: true
#           write: false
#           subscription_id: 12345678-1234-1234-1234-123456789012
#       policies:
#         - push-policy
#         - approval-policy
#         - plan-policy
#       roles:
#         - role_id: reader-role
#           space_id: root`;
